import { computed, ResourceRef, signal } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { MfaMethodSelectionEndpoint, MfaOtpAuthEndpoint } from '@clinic-endpoints';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { apiFinishedLoading, LocalStorageKeys } from '@shared-utils';
import { Auth } from '@types-lib';
import { AppEnvironment } from 'environments';
import { catchError, pipe, tap, throttleTime, throwError } from 'rxjs';
import { withToken } from './token.feature';

type MfaStoreFeatureState = {
  _mfaToken: string | null;
  _mfaMethod: Auth.MfaMethods | null;
  _mfaMethodValue: string | null;
};

type WithEnvironmentInput = {
  environment: AppEnvironment;
};

// multi factor authentication feature store
export function withMfa() {
  return signalStoreFeature(
    {
      props: type<WithEnvironmentInput>(),
    },
    withToken(),
    withProps(() => ({
      _mfaMethodSelectionEndpoint: new MfaMethodSelectionEndpoint(),
      _mfaOtpAuthEndpoint: new MfaOtpAuthEndpoint(),
      _changeMfaMethodTrigger: signal<Auth.MfaMethodSelectionRequest | undefined>(undefined),
      _mfaOtpAuthTrigger: signal<Auth.MfaOtpAuthRequest | undefined>(undefined),
    })),
    withState<MfaStoreFeatureState>({
      _mfaToken: null,
      _mfaMethod: null,
      _mfaMethodValue: null,
    }),
    withMethods(store => ({
      resetMfaMethod() {
        patchState(store, { _mfaMethod: null });
      },
      // setting an MFA methos automatically triggers a one time password send on the BE side
      setMfaMethod: rxMethod<Auth.MfaMethods>(
        pipe(
          throttleTime(500),
          tap(mfaMethod =>
            store._changeMfaMethodTrigger.update(() => ({
              mfa_token: store._mfaToken() as string,
              channel: mfaMethod,
            })),
          ),
        ),
      ),
      setMfaMethodValue(mfaMethodValue: string) {
        patchState(store, { _mfaMethodValue: mfaMethodValue });
      },
      resetMfaMethodValue() {
        patchState(store, { _mfaMethodValue: null });
      },
      mfaOtpLogin: rxMethod<Auth.MfaOtpAuth>(
        pipe(
          throttleTime(500),
          tap(({ code, addToTrustedDevices }) =>
            store._mfaOtpAuthTrigger.update(() => ({
              mfa_token: store._mfaToken() as string,
              code,
              add_to_trusted_device: addToTrustedDevices,
            })),
          ),
        ),
      ),
      setMfaToken(mfaToken: string | null) {
        patchState(store, { _mfaToken: mfaToken });
      },
      resetMfaToken() {
        patchState(store, { _mfaToken: null });
      },
    })),
    withProps(store => {
      const _changeMfaMethodResource: ResourceRef<Auth.MfaMethodSelectionResponse | undefined> = rxResource({
        params: () => store._changeMfaMethodTrigger(),
        stream: ({ params }) =>
          store._mfaMethodSelectionEndpoint.post(params as Auth.MfaMethodSelectionRequest).pipe(
            tap(({ sent_to }) => {
              patchState(store, { _mfaMethod: params?.channel, _mfaMethodValue: sent_to });
            }),
            catchError((err: Auth.MfaHttpErrorResponse) => {
              console.error('Failed to change MFA method:', err);
              return throwError(() => Object.assign(new Error('Something went wrong'), err));
            }),
          ),
      });

      const _mfaOtpVerifyResource: ResourceRef<Auth.MfaOtpAuthResponse | undefined> = rxResource({
        params: () => store._mfaOtpAuthTrigger(),
        stream: ({ params }) =>
          store._mfaOtpAuthEndpoint.post(params as Auth.MfaOtpAuthRequest).pipe(
            tap(response => {
              // remove isTempToken localstorage Flag after login
              if (store.isTempToken()) {
                store.setIsTempToken(false);
              }
              patchState(store, { _mfaToken: null });
              window.localStorage.setItem(LocalStorageKeys.TOKEN, response?.token);
              store.setToken(response.token);
            }),
            catchError((err: Auth.MfaHttpErrorResponse) => {
              console.log('MFA Login Error:', err);
              return throwError(() => Object.assign(new Error('Something went wrong'), err));
            }),
          ),
      });

      const _hasFinishedLoadingmfaOtpVerifyApi$ = toObservable(_mfaOtpVerifyResource.status).pipe(apiFinishedLoading());
      const _hasFinishedLoadingChangeMfaMethodApi$ = toObservable(_changeMfaMethodResource.status).pipe(
        apiFinishedLoading(),
      );

      return {
        changeMfaMethodResource: _changeMfaMethodResource.asReadonly(),
        mfaOtpVerifyResource: _mfaOtpVerifyResource.asReadonly(),
        hasFinishedLoadingmfaOtpVerifyApi$: _hasFinishedLoadingmfaOtpVerifyApi$,
        hasFinishedLoadingChangeMfaMethodApi$: _hasFinishedLoadingChangeMfaMethodApi$,
      };
    }),
    withComputed(({ _mfaToken, _mfaMethod, changeMfaMethodResource, mfaOtpVerifyResource, _mfaMethodValue }) => ({
      mfaMethod: computed(() => _mfaMethod()),
      mfaMethodValue: computed(() => _mfaMethodValue()),
      mfaToken: computed(() => _mfaToken()),
      changeMfaMethodFirstError: computed<Auth.MfaHttpErrorObject | undefined>(
        () => (changeMfaMethodResource.error() as Auth.MfaHttpErrorResponse)?.error?.errors?.[0],
      ),
      mfaOtpVerifyFirstError: computed<Auth.MfaHttpErrorObject | undefined>(
        () => (mfaOtpVerifyResource.error() as Auth.MfaHttpErrorResponse)?.error?.errors?.[0],
      ),
    })),
    withHooks({
      onDestroy(store) {
        store.setMfaMethod.destroy();
        store.mfaOtpLogin.destroy();
      },
    }),
  );
}
